/*
 * 聊天流式通道。`POST /api/v1/app/chat/stream`
 *
 * 传输选型：fetch + ReadableStream 解析 SSE 帧，不用 EventSource。
 * 原因有三：
 *   1. 发消息是 POST（带 clientMessageId 与消息体），EventSource 只能 GET；
 *   2. EventSource 不能带自定义头，requestId 只能塞进 query，会进日志；
 *   3. 需要自己控制重连时机与游标，EventSource 的自动重连不带 sequence。
 *
 * 断线语义：流中断后按 conversation sequence 补拉，而不是重发整轮——
 * 重发会重复扣灵力。补拉由调用方在 onError 后发起。
 *
 * 所有请求带 clientMessageId：超时重试不重复发送、不重复扣费。
 *
 * ## 没有总时长超时，只有空闲看门狗
 *
 * 一条流可以合法地开着几十秒（长回复逐字吐）。给它套一个**总时长**超时，
 * 结果必然是把正常的长回复误杀。所以这里一个总时长限制都没有。
 *
 * 但**空闲**看门狗是必须的，而且它不能只靠服务端。服务端那道空闲超时
 * （`BailianProvider.stream()`，30 秒）守的是「上游不下发」，它管不到
 * **下游这一侧断了**——微信 webview 切后台、中间代理静默掐连接的时候，
 * `reader.read()` 可以永远挂着不 resolve 也不 reject，`onClose` 一次都不会来。
 *
 * 那种情况下 `ChatPage` 的 `streaming` 会永远停在 true，而
 * `Composer` 的 `canSend` 带着 `!streaming`——输入框就此永久禁用。
 * 这个死锁以前由「停止」按钮兜着，那颗按钮 2026-08-01 删掉了
 * （它承诺的「这一轮不算了」服务端一件都做不到），出口换成了这个看门狗。
 *
 * ⚠ **阈值必须大于服务端那道 30 秒**（`LLM_TIMEOUT_MS.default`）。
 * 小于它就会抢在服务端的 `stream.error` 之前触发，把一条本来能说清
 * 「上游卡住了」的错误降级成一句笼统的「没等到」。
 */

import type { StreamEvent } from "@dreamverse/contracts";
import { ApiError, readCsrfToken } from "./api-client";

export interface SendMessageInput {
  /** 幂等键。重试必须复用同一个值。 */
  clientMessageId: string;
  text: string;
  imageAssetIds?: readonly string[];
}

export interface StreamHandlers {
  onEvent: (event: StreamEvent) => void;
  onError: (error: ApiError) => void;
  onClose: () => void;
}

/**
 * 多久没有任何字节到达就判定这条流卡死了。
 *
 * 45 秒 = 服务端空闲超时 30 秒 + 网络余量。正常路径永远走不到这里：
 * 上游真卡住时服务端会先发 `stream.error`，那条错误更具体。
 * 这个值只在**服务端的错误帧也送不过来**时才生效，也就是连接本身没了。
 */
const STALL_TIMEOUT_MS = 45_000;

/** 看门狗触发时的业务码。调用方据此与真正的传输错误区分开。 */
export const STREAM_STALLED = "STREAM_STALLED";

/**
 * 当前宿主能不能读流式响应体。
 *
 * 判据是 `Response.prototype.body`，不是 UA 嗅探。微信内置浏览器的版本
 * 分布极广，按 UA 猜只会猜错；而 `body` 缺失时 `openChatStream` 拿不到
 * reader，表现是"发出去了但永远收不到东西"——那种失败模式极难归因。
 *
 * 探测不到就走 `chat-api.ts` 的整句请求—响应。功能一样，只是没有逐字效果。
 */
export function streamingSupported(): boolean {
  return (
    typeof ReadableStream !== "undefined" &&
    typeof Response !== "undefined" &&
    "body" in Response.prototype
  );
}

const newRequestId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `req-${Date.now().toString(36)}`;

/**
 * 打开一条流。返回取消函数。
 *
 * **这个返回值不再是给「停止」按钮用的**——那颗按钮已经删了。
 * 现在只有两个调用方：组件卸载时的清理，与空闲看门狗。
 *
 * 取消只断开客户端；服务端那一轮照常跑完、照常扣费、照常落库。
 * 客户端不据此在界面上宣称「已取消」或「已退款」，因为两件都不成立。
 */
export function openChatStream(
  input: SendMessageInput,
  handlers: StreamHandlers,
): () => void {
  const controller = new AbortController();
  const requestId = newRequestId();
  // 这是个 POST，绕过了 apiRequest，CSRF 头得自己带。
  // 少了它服务端的 AppAuthGuard 会当场拒绝，且报的是 403 不是「流开不了」。
  const csrf = readCsrfToken();

  /*
   * 空闲看门狗。**从建连那一刻就开始计**，不是等拿到响应头才开始：
   * `fetch` 本身也可以在网络死掉时永远挂着，那一段同样要有出口。
   *
   * `stalled` 与 `controller.signal.aborted` 不能互相替代：卸载时的取消
   * 也会把 aborted 置真，但那时没人需要收到错误。
   */
  let stalled = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const armIdle = (): void => {
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      stalled = true;
      controller.abort();
    }, STALL_TIMEOUT_MS);
  };
  const disarmIdle = (): void => {
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = null;
  };
  const stalledError = (): ApiError =>
    new ApiError({
      kind: "timeout",
      message: `${Math.round(STALL_TIMEOUT_MS / 1000)} 秒没有收到任何内容`,
      requestId,
      code: STREAM_STALLED,
      retryable: true,
    });

  armIdle();

  void (async () => {
    let response: Response;
    try {
      // ⚠ 必须在 `/api/v1/app` 前缀下：Refresh Cookie 的作用域收窄到这里，
      // 挂在外面的接口在 Access Token 过期后只能 401。
      response = await fetch("/api/v1/app/chat/stream", {
        method: "POST",
        signal: controller.signal,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          "X-Request-Id": requestId,
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify(input),
      });
    } catch {
      disarmIdle();
      if (stalled) {
        handlers.onError(stalledError());
        handlers.onClose();
        return;
      }
      if (controller.signal.aborted) {
        handlers.onClose();
        return;
      }
      handlers.onError(
        new ApiError({
          kind: "offline",
          message: "无法建立流式连接",
          requestId,
          retryable: true,
        }),
      );
      handlers.onClose();
      return;
    }

    if (!response.ok || !response.body) {
      disarmIdle();
      handlers.onError(
        new ApiError({
          kind: response.ok ? "malformed" : "http",
          message: response.ok
            ? "服务端未返回流式响应体"
            : `服务端返回 ${response.status}`,
          requestId,
          status: response.status,
          retryable: !response.ok && response.status >= 500,
        }),
      );
      handlers.onClose();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        // 收到字节就说明连接还活着。重置的粒度是 chunk 不是帧——
        // 服务端在两个事件之间可以合法地什么都不发（她在想），
        // 而那段沉默不该被算成卡死。
        armIdle();

        buffer += decoder.decode(value, { stream: true });

        // SSE 以空行分帧。跨 chunk 的半个帧留在 buffer 里等下一轮。
        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("\n\n");

          const payload = frame
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trim())
            .join("");

          if (!payload || payload === "[DONE]") continue;

          try {
            handlers.onEvent(JSON.parse(payload) as StreamEvent);
          } catch {
            // 单帧解析失败不该炸掉整条流，但要如实上报，不静默吞掉。
            handlers.onError(
              new ApiError({
                kind: "malformed",
                message: "流中出现无法解析的帧",
                requestId,
                retryable: false,
              }),
            );
          }
        }
      }
    } catch {
      if (stalled) {
        handlers.onError(stalledError());
      } else if (!controller.signal.aborted) {
        handlers.onError(
          new ApiError({
            kind: "offline",
            message: "流式连接中断",
            requestId,
            retryable: true,
          }),
        );
      }
    } finally {
      disarmIdle();
      handlers.onClose();
    }
  })();

  return () => {
    disarmIdle();
    controller.abort();
  };
}
